import { Controller, useForm, useWatch } from 'react-hook-form';

import { Radio } from 'antd';

import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { FormBlock } from '@/components/FormBlock';
import { useZustandSync } from '@/hooks/useZustandSync';
import { isValidCNPJ, isValidCPF } from '@/lib/functions';
import { productStore } from '@/stores/productStore';

import { CPFOrCPNJInput } from '../form/CPFOrCPNJInput';
import { Input } from '../form/Input';

const producerDocumentSchema = z
  .object({
    producerNative: z.boolean(),
    producerDocument: z.string().min(5, 'O documento deve ter pelo menos 5 caracteres.').nonempty()
  })
  .superRefine((data, ctx) => {
    const isNative = data.producerNative;
    const cleanedValue = data.producerDocument.replace(/\D/g, '');

    if (!isNative) {
      return;
    }

    if (cleanedValue.length != 11 && cleanedValue.length != 14) {
      ctx.addIssue({
        code: 'custom',
        message: 'Documento inválido',
        path: ['producerDocument']
      });

      return;
    }

    if (cleanedValue.length == 11) {
      if (!isValidCPF(data.producerDocument)) {
        ctx.addIssue({
          code: 'custom',
          message: 'CPF inválido',
          path: ['producerDocument']
        });
      }
    }

    if (cleanedValue.length == 14) {
      if (!isValidCNPJ(data.producerDocument)) {
        ctx.addIssue({
          code: 'custom',
          message: 'CNPJ inválido',
          path: ['producerDocument']
        });
      }
    }
  });

type ProducerDocumentFormValues = z.infer<typeof producerDocumentSchema>;

export default function ProducerDocument() {
  const form = useForm<ProducerDocumentFormValues>({
    resolver: zodResolver(producerDocumentSchema),
    mode: 'onChange',
    defaultValues: {
      producerNative: productStore.getState().producer.native,
      producerDocument: productStore.getState().producer.document
    }
  });

  useZustandSync<ProducerDocumentFormValues>(form.control, (data, current) => ({
    producer: {
      ...(current.producer || {}),
      native: data.producerNative,
      document: data.producerDocument
    }
  }));

  const native = useWatch({
    name: 'producerNative',
    control: form.control
  });

  return (
    <FormBlock name='producer-data' form={form} title='Documento'>
      <Controller
        control={form.control}
        name='producerNative'
        rules={{ required: true }}
        render={({ field }) => (
          <Radio.Group
            className='flex! flex-col gap-1 [&_.ant-radio]:mt-0.5! [&_.ant-radio]:self-auto! [&_.ant-radio-label]:w-full [&_.ant-radio-wrapper]:me-0! [&_.ant-radio-wrapper]:flex! [&_.ant-radio-wrapper]:items-start!'
            options={[
              {
                value: true,
                label: (
                  <div className='flex w-full flex-col gap-1'>
                    <div>Sou Brasileiro</div>
                    {native && (
                      <div>
                        <CPFOrCPNJInput label='CPF/CNPJ' name='producerDocument' />
                      </div>
                    )}
                  </div>
                )
              },
              {
                value: false,
                label: (
                  <div className='flex w-full flex-col gap-1'>
                    <div>Sou Estrangeiro</div>
                    {!native && (
                      <div>
                        <Input label='Passaporte' id='passport' name='producerDocument' />
                      </div>
                    )}
                  </div>
                )
              }
            ]}
            {...field}
          />
        )}
      />
    </FormBlock>
  );
}
